import type { FC } from 'react';
import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { styled } from '@compiled/react';

import type { DocNode } from '@atlaskit/adf-schema';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button';

import { ADFRenderer } from '@confluence/adf-renderer/entry-points/ADFRenderer.ts';

import { COMMENT_COLLAPSE_THRESHOLD } from '../feedConstants.ts';

import { FeedCardContext } from './FeedCard/FeedCardContext.ts';

const i18n = defineMessages({
	showMore: {
		id: 'feed.card.comment.showMore',
		defaultMessage: 'Show more',
		description: 'Button text to show more of the comment',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentWrapper = styled.div<{ collapsed: boolean }>({
	position: 'relative',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentBlur = styled.div({
	backgroundImage: `linear-gradient( 180deg, ${token('utility.UNSAFE.transparent')} 0%, ${token('elevation.surface.raised')} 77.6% )`,
	height: '24px',
	width: '100%',
	bottom: token('space.negative.025'),
	position: 'absolute',
	zIndex: 1,
	display: 'flex',
	justifyContent: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ExpandContent = styled.div({
	background: token('color.background.input'),
	alignSelf: 'flex-end',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ClearFix = styled.div({
	clear: 'both',
});

export type RenderedContentOptions = {
	id: string;
	body: {
		value: string;
		format?: string;
	};
};

type RenderedContentProps = {
	content: RenderedContentOptions;
};

export const RenderedContent: FC<RenderedContentProps> = ({ content }) => {
	const contentId = content.id;
	const adfValue: object =
		(!content.body.format || content.body.format === 'ADF') && JSON.parse(content.body.value);
	const [contentEl, setContentEl] = useState<HTMLDivElement | null>(null);
	const [collapsed, setCollapsed] = useState(false);
	const observerRef = useRef<ResizeObserver | null>(null);
	const { fireAnalyticsEvent } = useContext(FeedCardContext);

	useEffect(() => {
		if (contentEl) {
			if (!observerRef.current) {
				observerRef.current = new ResizeObserver((entries) => {
					if (entries[0].contentRect.height > COMMENT_COLLAPSE_THRESHOLD) {
						setCollapsed(true);
					}
				});
			}
			observerRef.current.observe(contentEl);
		}
	}, [contentEl, observerRef]);

	const expandContent = useCallback(() => {
		observerRef.current?.disconnect();
		setCollapsed(false);
		fireAnalyticsEvent({
			type: 'sendUIEvent',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'seeMore',
			commentId: contentId,
			contentId,
		});
	}, [observerRef, setCollapsed, contentId, fireAnalyticsEvent]);

	return (
		<ContentWrapper ref={setContentEl} collapsed={collapsed}>
			{content.body.format === 'TEXT' ? (
				<div>{content.body.value}</div>
			) : (
				<ADFRenderer
					document={adfValue as DocNode}
					portal={contentEl ?? undefined}
					contentId={contentId}
					isTruncatedContent={collapsed}
					fadeOutHeight={0}
					maxHeight={COMMENT_COLLAPSE_THRESHOLD}
					allowStickyHeaders={false}
					appearance="comment"
				/>
			)}

			{/*need the custom blur section because the editors `useContentFade` is hardcoded to go transparent -> white. doesnt work with our grey background*/}
			{collapsed && (
				<ContentBlur>
					<ExpandContent>
						{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
						<Button onClick={expandContent} appearance="subtle-link" spacing="none">
							&#8230;
							<FormattedMessage {...i18n.showMore} />
						</Button>
					</ExpandContent>
				</ContentBlur>
			)}

			{/*fixes floated elements in the renderer*/}
			<ClearFix />
		</ContentWrapper>
	);
};
